/**
 * DAVIDsTEA wishlist.
 *
 * The theme draws every heart (snippets/dt-wishlist-button) and this file saves and removes
 * through the app proxy at /apps/wishlist. Shopify signs each proxied request with the logged-in
 * customer's id, and the backend writes the customer metafield custom.wishlist (a list of
 * product references) — the storefront cannot write a customer metafield any other way.
 *
 *   1. The hearts. A click flips every heart for that product at once — the page's own and any
 *      card further down for the same tea — and the server's answer, the whole list, is what
 *      every heart is redrawn from. A failure therefore puts the hearts back by itself.
 *
 *   2. Guests. A guest's click opens the login dialog (snippets/dt-wishlist). Its button goes to
 *      /account/login?return_url=…, which keeps the language and the page; the product rides
 *      along in that URL as dt_wishlist_add and is saved on the way back.
 *
 *   3. Cards drawn later — the next page of a collection, the quick-add modal, recently viewed,
 *      a section the theme editor re-renders — are picked up by a MutationObserver.
 *
 * Products are keyed by id, never by handle: handles are translated per language here.
 *
 * Classic script, deferred, loaded once from snippets/dt-wishlist. The hearts stay hidden until
 * it starts (.dt-wishlist-ready), so a shopper is never offered a control that does nothing.
 */
(() => {
  if (window.__dtWishlist) return;
  window.__dtWishlist = true;

  const stateElement = document.getElementById('dt-wishlist-state');
  if (!stateElement) return;

  let state;
  try {
    state = JSON.parse(stateElement.textContent);
  } catch (error) {
    console.warn('[dt-wishlist] Could not read the wishlist state:', error);
    return;
  }

  const HEART = '[data-dt-wishlist]';
  const READY_CLASS = 'dt-wishlist-ready';
  const ADD_PARAM = 'dt_wishlist_add';
  const STORAGE_KEY = 'dt-wishlist:ids';
  const TOAST_MS = 4000;

  /** The saved product ids, as strings. */
  let saved = new Set((state.ids || []).map(String));

  /** What a guest was trying to save when the login dialog opened. */
  let pendingAdd = null;
  let pendingButton = null;

  /* Read now and dropped from the address bar straight away, so a reload or a shared link
     cannot save the product a second time. */
  const returningWith = takeParam(ADD_PARAM);

  const channel = 'BroadcastChannel' in window ? new BroadcastChannel('dt-wishlist') : null;

  /* ------------------------------------------------------------------------------------------
     State
     ------------------------------------------------------------------------------------------ */

  function paint(button, isSaved) {
    button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    button.classList.toggle('dt-wishlist__button--saved', isSaved);
    const label = isSaved ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) {
      button.setAttribute('aria-label', label);
      // The product page heart has its own hover note, and a native title would pop up a
      // second tooltip over it.
      if (!button.closest('.dt-wishlist__wrap')) button.setAttribute('title', label);
    }
  }

  function render() {
    document.querySelectorAll(HEART).forEach((button) => {
      if (button.dataset.busy) return;
      paint(button, saved.has(button.dataset.productId));
    });
  }

  let renderQueued = false;
  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => {
      renderQueued = false;
      render();
    });
  }

  /** Takes the server's list as the truth, shares it with other tabs and the rest of the page. */
  function setSaved(ids, { broadcast = true } = {}) {
    saved = new Set(ids.map(String));
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...saved]));
    } catch {}
    if (broadcast) channel?.postMessage([...saved]);
    document.dispatchEvent(new CustomEvent('dt:wishlist:change', { detail: { ids: [...saved] } }));
    queueRender();
  }

  /* ------------------------------------------------------------------------------------------
     Saving and removing
     ------------------------------------------------------------------------------------------ */

  class WishlistError extends Error {
    constructor(code) {
      super(code);
      this.code = code;
    }
  }

  async function send(productId, action) {
    let response;
    try {
      response = await fetch(`${state.proxy}/toggle`, {
        method: 'POST',
        // The ngrok header only matters while the backend sits behind a free ngrok domain, whose
        // warning page would otherwise answer in its place (the proxy passes the shopper's
        // User-Agent through). Harmless anywhere else; drop it once the backend has its own domain.
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'ngrok-skip-browser-warning': '1' },
        body: JSON.stringify({ product_id: productId, action }),
      });
    } catch {
      throw new WishlistError('network');
    }

    let body = null;
    try {
      body = await response.json();
    } catch {}

    if (!response.ok || !body?.ok) throw new WishlistError(body?.error || `http_${response.status}`);
    return body;
  }

  /** Why the save failed, as the toast that reports it. */
  function toastFor(code) {
    if (code === 'wishlist_full') return 'full';
    if (code === 'login_required') return 'session';
    return 'error';
  }

  async function change(productId, adding) {
    /* Every heart for this product answers at once, and the list puts them right if the
       server refuses. */
    const twins = [...document.querySelectorAll(HEART)].filter((b) => b.dataset.productId === productId);
    twins.forEach((b) => {
      b.dataset.busy = 'true';
      paint(b, adding);
    });

    try {
      const body = await send(productId, adding ? 'add' : 'remove');
      setSaved(body.ids || []);
      notify(adding ? 'added' : 'removed');
    } catch (error) {
      console.warn('[dt-wishlist]', error.code || error);
      notify(toastFor(error.code));
    } finally {
      twins.forEach((b) => delete b.dataset.busy);
      render();
    }
  }

  function toggle(button) {
    if (button.dataset.busy) return;
    const productId = button.dataset.productId;
    if (!productId) return;

    if (!state.loggedIn) {
      openLogin(button, productId);
      return;
    }

    change(productId, !saved.has(productId));
  }

  /* The product a guest tried to save before logging in, saved now that they have. */
  function finishPendingAdd() {
    if (!returningWith || !state.loggedIn) return;
    if (!/^\d{1,20}$/.test(returningWith) || saved.has(returningWith)) return;
    change(returningWith, true);
  }

  /* ------------------------------------------------------------------------------------------
     The login dialog
     ------------------------------------------------------------------------------------------ */

  let dialog = null;

  function mountDialog() {
    if (dialog) return dialog;
    const template = document.getElementById('dt-wishlist-login');
    if (!template) return null;
    dialog = template.content.firstElementChild.cloneNode(true);
    document.body.append(dialog);

    dialog.querySelector('[data-close]')?.addEventListener('click', () => dialog.close());
    /* A click on the backdrop lands on the dialog itself. */
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      pendingAdd = null;
      pendingButton?.focus();
      pendingButton = null;
    });
    return dialog;
  }

  function openLogin(button, productId) {
    const element = mountDialog();
    if (!element) return;

    pendingAdd = productId;
    pendingButton = button;

    const back = withParam(window.location.pathname + window.location.search, ADD_PARAM, productId);
    const login = element.querySelector('[data-login]');
    if (login) login.href = loginUrl(back);

    if (!element.open) element.showModal();
    login?.focus();
  }

  function loginUrl(returnPath) {
    return `${state.loginBase}?return_url=${encodeURIComponent(returnPath)}`;
  }

  /* ------------------------------------------------------------------------------------------
     The toast
     ------------------------------------------------------------------------------------------ */

  let toast = null;
  let toastTimer = 0;

  /* Built once and left in the page empty, so the status role is already there when the text
     arrives — a live region inserted together with its message is often not announced. */
  function mountToast() {
    const template = document.getElementById('dt-wishlist-toast');
    if (!template || toast) return;
    toast = template.content.firstElementChild.cloneNode(true);
    document.body.append(toast);
  }

  /** @param {'added' | 'removed' | 'error' | 'full' | 'session'} kind */
  function notify(kind) {
    mountToast();
    if (!toast) return;

    if (kind === 'session') {
      const login = toast.querySelector('[data-link="login"]');
      if (login) login.href = loginUrl(window.location.pathname + window.location.search);
    }

    toast.querySelector('.dt-wishlist-toast__text').textContent = toast.dataset[kind] || '';
    toast.dataset.kind = kind;
    toast.setAttribute('data-open', '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.removeAttribute('data-open'), TOAST_MS);
  }

  /* ------------------------------------------------------------------------------------------
     Start
     ------------------------------------------------------------------------------------------ */

  /* Capture phase, so the card's full-size overlay link never sees the click. */
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest(HEART) : null;
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      toggle(button);
    },
    true
  );

  channel?.addEventListener('message', (event) => {
    if (Array.isArray(event.data)) setSaved(event.data, { broadcast: false });
  });

  /* Back and forward restore the page as it was, list and all; the last list this tab saw is
     the better answer. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (Array.isArray(stored)) setSaved(stored, { broadcast: false });
    } catch {}
  });

  function start() {
    document.documentElement.classList.add(READY_CLASS);
    mountToast();
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...saved]));
    } catch {}
    render();
    finishPendingAdd();

    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element && (node.matches(HEART) || node.querySelector(HEART))) {
            queueRender();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  /* ------------------------------------------------------------------------------------------
     URL helpers
     ------------------------------------------------------------------------------------------ */

  function takeParam(name) {
    const url = new URL(window.location.href);
    const value = url.searchParams.get(name);
    if (value === null) return null;
    url.searchParams.delete(name);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    return value;
  }

  function withParam(path, name, value) {
    const url = new URL(path, window.location.origin);
    url.searchParams.set(name, value);
    return url.pathname + url.search;
  }
})();
